/**
 * Hook to check if the current user can view engagement details
 * based on workspace privacy settings and participant membership.
 *
 * When publicVisibility is false (default), users can only see
 * engagement details if they are a participant — identified by
 * user ID in participantUserIds/userId, or email in contacts/users.
 */

import { useWorkspacePreferences } from './use-workspace-preferences';
import { useCurrentUser } from './use-current-user';
import { isLovablePreview } from '@/lib/utils/environment';
import { useIsAdminOrOwner } from '@/lib/rbac/hooks';

export interface EngagementPrivacyData {
  participantUserIds?: (number | null)[] | null;
  userId?: number | null;
  contacts?: Array<{ primaryEmail?: string | null }> | null;
  users?: Array<{ email?: string | null }> | null;
}

export const useEngagementPrivacy = (
  engagement: EngagementPrivacyData | null | undefined,
) => {
  const { workspacePreferences, loading: prefsLoading } = useWorkspacePreferences();
  const { currentUser, loading: userLoading } = useCurrentUser();
  const isAdminOrOwner = useIsAdminOrOwner();

  // Preview mode: always allow
  if (isLovablePreview()) {
    return { canView: true, loading: false };
  }

  const loading = prefsLoading || userLoading;

  // Still loading dependencies
  if (loading || !currentUser) {
    return { canView: false, loading: true };
  }

  // Public workspace: everyone can see everything
  if (workspacePreferences?.publicVisibility === true) {
    return { canView: true, loading: false };
  }

  // 'everyone' shows every engagement; 'admins_and_owners' shows every
  // engagement to admins/owners (superusers resolve to owner). Everyone else
  // falls through to the participant check — detail sheets fetch by id, which
  // older backends answered without applying the tier.
  const emailVisibility = workspacePreferences?.emailVisibility;
  if (
    emailVisibility === 'everyone' ||
    (emailVisibility === 'admins_and_owners' && isAdminOrOwner)
  ) {
    return { canView: true, loading: false };
  }

  // Engagement data not loaded yet
  if (!engagement) {
    return { canView: false, loading: true };
  }

  const numericUserId = Number(currentUser.id);
  const userEmail = currentUser.email?.toLowerCase();

  // Check participantUserIds
  if (
    engagement.participantUserIds?.some(
      (id) => id != null && id === numericUserId,
    )
  ) {
    return { canView: true, loading: false };
  }

  // Check engagement owner (userId)
  if (engagement.userId != null && engagement.userId === numericUserId) {
    return { canView: true, loading: false };
  }

  // Check contacts by email
  if (
    userEmail &&
    engagement.contacts?.some(
      (c) => c.primaryEmail?.toLowerCase() === userEmail,
    )
  ) {
    return { canView: true, loading: false };
  }

  // Check users by email (when EngagementRelations fragment is present)
  if (
    userEmail &&
    engagement.users?.some((u) => u.email?.toLowerCase() === userEmail)
  ) {
    return { canView: true, loading: false };
  }

  return { canView: false, loading: false };
};
